// src/components/WhyNetwoke.jsx
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// ─── Canvas particles ─────────────────────────────────────────────────────────
function ParticleCanvas() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    const T = 'rgba(0,212,184,', B = 'rgba(77,157,224,'
    let nodes = [], raf
    function resize() {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      nodes = Array.from({ length: 50 }, () => ({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        vx: (Math.random() - .5) * .22, vy: (Math.random() - .5) * .22,
        r: Math.random() * 1.6 + .4, c: Math.random() > .5 ? T : B,
        p: Math.random() * Math.PI * 2,
      }))
    }
    resize()
    window.addEventListener('resize', resize)
    let lastFrame = 0
    function draw(ts) {
      // Cap at 20fps — background particles don't need more
      if (ts - lastFrame < 50) { raf = requestAnimationFrame(draw); return }
      lastFrame = ts
      // Pause when tab not visible or window not focused
      if (document.hidden || !document.hasFocus()) { raf = requestAnimationFrame(draw); return }
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (let i = 0; i < nodes.length; i++) for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y
        const d = Math.sqrt(dx * dx + dy * dy)
        if (d < 120) {
          const a = (1 - d / 120) * .12
          ctx.strokeStyle = nodes[i].c + a + ')'
          ctx.lineWidth = .4
          ctx.beginPath()
          ctx.moveTo(nodes[i].x, nodes[i].y)
          ctx.lineTo(nodes[j].x, nodes[j].y)
          ctx.stroke()
        }
      }
      for (const n of nodes) {
        const a = .3 + Math.sin(n.p) * .2
        n.p += .02
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2)
        ctx.fillStyle = n.c + a + ')'; ctx.fill()
        n.x += n.vx; n.y += n.vy
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1
      }
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf) }
  }, [])
  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
}

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ value, suffix = '' }) {
  const [display, setDisplay] = useState(0)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return
      observer.disconnect()
      let start = 0
      const end = parseInt(value)
      const duration = 1200
      const step = duration / end
      const timer = setInterval(() => {
        start += 1
        setDisplay(start)
        if (start >= end) clearInterval(timer)
      }, step)
    }, { threshold: 0.5 })
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [value])

  return <span ref={ref} style={{ fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Courier New', monospace", letterSpacing: '0.02em' }}>{display}{suffix}</span>
}

// ─── Comparison table — beautiful, mobile-first ───────────────────────────────
const ROWS = [
  { icon: '🎯', label: 'Primary goal',             competitor: 'Block or allow connections',       netwoke: 'Understand + explain connections'     },
  { icon: '🤖', label: 'Approach',                 competitor: 'Rule-based firewall',               netwoke: 'AI-powered intelligence'               },
  { icon: '❓', label: 'Unknown app connects',     competitor: '"Allow or Deny?" — you decide',     netwoke: 'Explains in plain English what it is'  },
  { icon: '📈', label: 'Learning curve',           competitor: 'Steep — requires network knowledge', netwoke: 'Minimal — designed for everyone'        },
  { icon: '🔔', label: 'Alert style',              competitor: 'Every single connection',           netwoke: 'Only what actually matters'            },
  { icon: '🔒', label: 'Data privacy',             competitor: 'Some telemetry collected',          netwoke: '100% local — nothing leaves your Mac'  },
  { icon: '⚡', label: 'Kill suspicious process',  competitor: 'Not available',                     netwoke: 'One click from the dashboard'          },
  { icon: '💰', label: 'Pricing',                  competitor: '$69/year subscription',             netwoke: '$11.99 one time, forever'              },
  { icon: '👤', label: 'Who it\'s for',            competitor: 'Network professionals only',        netwoke: 'Every Mac user'                        },
]

function ComparisonTable() {
  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px 72px' }}>

      {/* Section label */}
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#00d4b8', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 14 }}>
          Head to head
        </div>
        <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 38px)', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1, margin: '0 0 14px' }}>
          The difference is philosophy
        </h2>
        <p style={{ fontSize: 15, color: 'rgba(220,232,245,0.45)', fontWeight: 300, maxWidth: 480, margin: '0 auto' }}>
          Other tools ask you to make decisions. Netwoke helps you understand them.
        </p>
      </div>

      {/* ── DESKTOP TABLE (hidden on mobile) ── */}
      <div className="comp-table-desktop" style={{ background: 'linear-gradient(180deg, #080f1a 0%, #060c16 100%)', borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}>

        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ padding: '18px 24px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'rgba(220,232,245,0.25)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Feature
          </div>
          <div style={{ padding: '18px 24px', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(220,232,245,0.4)' }}>Competitors</div>
            <div style={{ fontSize: 10, color: 'rgba(220,232,245,0.2)', fontFamily: "'IBM Plex Mono', monospace", marginTop: 3 }}></div>
          </div>
          <div style={{ padding: '18px 24px', borderLeft: '1px solid rgba(0,212,184,0.15)', background: 'rgba(0,212,184,0.04)', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #00d4b8, #4d9de0)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#00d4b8', boxShadow: '0 0 10px #00d4b8' }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: '#00d4b8' }}>Netwoke</div>
            </div>
            <div style={{ fontSize: 10, color: 'rgba(0,212,184,0.5)', fontFamily: "'IBM Plex Mono', monospace", marginTop: 3 }}></div>
          </div>
        </div>

        {/* Rows */}
        {ROWS.map((row, i) => (
          <div key={row.label} style={{
            display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr',
            borderBottom: i < ROWS.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
            transition: 'background 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {/* Label */}
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{row.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(220,232,245,0.7)', lineHeight: 1.4 }}>{row.label}</span>
            </div>

            {/* Competitor */}
            <div style={{ padding: '16px 24px', borderLeft: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ color: '#ef4444', fontSize: 9, fontWeight: 700 }}>✕</span>
              </div>
              <span style={{ fontSize: 12, color: 'rgba(220,232,245,0.4)', lineHeight: 1.4 }}>{row.competitor}</span>
            </div>

            {/* Netwoke */}
            <div style={{ padding: '16px 24px', borderLeft: '1px solid rgba(0,212,184,0.08)', background: 'rgba(0,212,184,0.02)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(0,212,184,0.15)', border: '1px solid rgba(0,212,184,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ color: '#00d4b8', fontSize: 9, fontWeight: 700 }}>✓</span>
              </div>
              <span style={{ fontSize: 12, color: '#dce8f5', lineHeight: 1.4, fontWeight: 500 }}>{row.netwoke}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── MOBILE CARDS (hidden on desktop) ── */}
      <div className="comp-table-mobile" style={{ display: 'none', flexDirection: 'column', gap: 12 }}>
        {ROWS.map((row) => (
          <div key={row.label} style={{
            background: '#080f1a', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 14, overflow: 'hidden',
          }}>
            {/* Card header */}
            <div style={{ padding: '14px 16px', background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{row.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#dce8f5' }}>{row.label}</span>
            </div>

            {/* Two columns */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              {/* Competitor */}
              <div style={{ padding: '14px 14px', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", color: 'rgba(220,232,245,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Others</div>
                <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <span style={{ color: '#ef4444', fontSize: 8, fontWeight: 700 }}>✕</span>
                  </div>
                  <span style={{ fontSize: 11, color: 'rgba(220,232,245,0.4)', lineHeight: 1.5 }}>{row.competitor}</span>
                </div>
              </div>

              {/* Netwoke */}
              <div style={{ padding: '14px 14px', background: 'rgba(0,212,184,0.03)', borderLeft: '1px solid rgba(0,212,184,0.1)' }}>
                <div style={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", color: '#00d4b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Netwoke</div>
                <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: 'rgba(0,212,184,0.15)', border: '1px solid rgba(0,212,184,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <span style={{ color: '#00d4b8', fontSize: 8, fontWeight: 700 }}>✓</span>
                  </div>
                  <span style={{ fontSize: 11, color: '#dce8f5', lineHeight: 1.5, fontWeight: 500 }}>{row.netwoke}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @media (max-width: 640px) {
          .comp-table-desktop { display: none !important; }
          .comp-table-mobile  { display: flex !important; }
        }
      `}</style>
    </div>
  )
}

// ─── Scenario card ────────────────────────────────────────────────────────────
function ScenarioCard({ app, competitorResponse, netwokeResponse }) {
  const [active, setActive] = useState('netwoke')
  return (
    <div style={{ background: '#080f1a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>
      {/* Toggle */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        {[['competitor', 'Other Tools'], ['netwoke', 'Netwoke']].map(([id, label]) => (
          <button key={id} onClick={() => setActive(id)} style={{
            flex: 1, padding: '12px', border: 'none', cursor: 'pointer',
            background: active === id
              ? id === 'netwoke' ? 'rgba(0,212,184,0.1)' : 'rgba(255,255,255,0.04)'
              : 'transparent',
            color: active === id
              ? id === 'netwoke' ? '#00d4b8' : 'rgba(220,232,245,0.6)'
              : 'rgba(220,232,245,0.3)',
            fontSize: 12, fontWeight: 600,
            fontFamily: "'IBM Plex Sans', sans-serif",
            borderBottom: active === id
              ? `2px solid ${id === 'netwoke' ? '#00d4b8' : 'rgba(220,232,245,0.3)'}`
              : '2px solid transparent',
            transition: 'all 0.2s',
          }}>{label}</button>
        ))}
      </div>

      {/* Scenario header */}
      <div style={{ padding: '16px 20px 0', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: 'rgba(220,232,245,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Scenario
      </div>
      <div style={{ padding: '6px 20px 16px', fontSize: 14, fontWeight: 600, color: '#dce8f5' }}>
        {app}
      </div>

      {/* Response */}
      <div style={{ padding: '0 20px 20px' }}>
        {active === 'competitor' ? (
          <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#ef4444', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Other Tools say</div>
            <p style={{ fontSize: 13, color: 'rgba(220,232,245,0.5)', lineHeight: 1.7, margin: 0, fontFamily: "'IBM Plex Sans', sans-serif" }}>{competitorResponse}</p>
          </div>
        ) : (
          <div style={{ background: 'rgba(0,212,184,0.06)', border: '1px solid rgba(0,212,184,0.2)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#00d4b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Netwoke says</div>
            <p style={{ fontSize: 13, color: '#dce8f5', lineHeight: 1.7, margin: 0, fontFamily: "'IBM Plex Sans', sans-serif" }}>{netwokeResponse}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Feature pill ─────────────────────────────────────────────────────────────
function Pill({ icon, text, color = '#00d4b8' }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      padding: '7px 14px', borderRadius: 100,
      background: `${color}12`, border: `1px solid ${color}30`,
      fontSize: 12, color, fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 500,
    }}>
      <span>{icon}</span> {text}
    </div>
  )
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function WhyNetwoke() {
  const navigate = useNavigate()

  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <>
      <style>{`
        :root {
          --black:#04060d;--deep:#080c16;--surface:#0d1220;--elevated:#121a2e;
          --border:rgba(255,255,255,0.07);--border2:rgba(255,255,255,0.12);
          --teal:#00d4b8;--blue:#4d9de0;--green:#3ecf8e;--amber:#f5a623;--red:#ef4444;
          --text:#dce8f5;--muted:rgba(220,232,245,0.5);--faint:rgba(220,232,245,0.25);
          --font-display:'IBM Plex Sans',sans-serif;
          --font-mono:'IBM Plex Mono',monospace;
          --font-body:'IBM Plex Sans',sans-serif;
        }
        .nw-root{background:var(--black);color:var(--text);font-family:var(--font-body);
          -webkit-font-smoothing:antialiased;min-height:100vh;
          overflow-x:hidden; overflow-y:auto;}
        .nw-root *{box-sizing:border-box;margin:0;padding:0;}
        .nw-page{position:relative;z-index:2;}
        .grad{background:linear-gradient(135deg,var(--teal) 0%,var(--blue) 60%);
          -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
      `}</style>
      <div className="nw-root">
      <ParticleCanvas />
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* ── NAV ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px',
        background: 'rgba(4,6,13,0.92)', backdropFilter: 'blur(12px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        </div>
        <button onClick={() => navigate('/')} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          background: 'none', border: 'none',
          color: '#00d4b8', fontSize: 13, cursor: 'pointer',
          fontFamily: '"IBM Plex Mono", monospace', fontWeight: 500,
          transition: 'all .2s',
        }}
          onMouseEnter={e => { 
            e.currentTarget.style.background='rgba(0,212,184,0.1)'; 
            e.currentTarget.style.color='#00d4b8';
          }}
          onMouseLeave={e => { 
            e.currentTarget.style.background='none'; 
            e.currentTarget.style.color='#00d4b8';
          }}>← Back to site</button>
      </div>

      {/* ── HERO ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '80px 28px 0', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '5px 16px', borderRadius: 20,
          background: 'rgba(0,212,184,0.08)', border: '1px solid rgba(0,212,184,0.25)',
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          color: '#00d4b8', letterSpacing: '0.1em', textTransform: 'uppercase',
          marginBottom: 28,
        }}>
          Network Intelligence vs Network Firewall
        </div>

        <h1 style={{
          fontSize: 'clamp(32px, 5vw, 58px)', fontWeight: 700,
          lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 20,
        }}>
          Why smart Mac users{' '}
          <span style={{ background: 'linear-gradient(90deg, #00d4b8, #4d9de0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            choose Netwoke
          </span>
        </h1>

        <p style={{ fontSize: 18, color: 'rgba(220,232,245,0.55)', lineHeight: 1.75, fontWeight: 300, maxWidth: 600, margin: '0 auto 48px' }}>
          Other network tools ask you to make hundreds of decisions you don't have the context to make. Netwoke makes those decisions understandable — and does it in plain English.
        </p>

              </div>

      {/* ── HEAD TO HEAD ── */}
      <ComparisonTable />

      
      {/* ── WHO IS NETWOKE FOR ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 20px' }}>
        <div className="comparison-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
          {/* Other Tools */}
          <div className="comparison-card" style={{ background: '#080f1a', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '24px 20px', transition: 'all 0.3s ease', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.border='1px solid rgba(255,255,255,0.15)'; e.currentTarget.style.boxShadow='0 0 20px rgba(255,255,255,0.1), 0 0 40px rgba(255,255,255,0.05)'; e.currentTarget.style.transform='translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.border='1px solid rgba(255,255,255,0.07)'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)' }}>
            <div className="comparison-title" style={{ fontSize: 16, fontWeight: 600, color: 'rgba(220,232,245,0.4)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(220,232,245,0.2)' }} />
              Choose Other Tools if…
            </div>
            {[
              'You want granular control over every single connection',
              'You enjoy building and managing complex firewall rules',
              'You\'re a network professional who understands ports and protocols',
              'You prefer traditional security software interfaces',
              'You have time to research every unknown connection yourself',
            ].map((text, i) => (
              <div key={i} className="comparison-item" style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(220,232,245,0.2)', marginTop: 6, flexShrink: 0 }} />
                <span className="comparison-text" style={{ fontSize: 14, color: 'rgba(220,232,245,0.45)', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>

          {/* Netwoke */}
          <div className="comparison-card" style={{ background: 'rgba(0,212,184,0.04)', border: '1px solid rgba(0,212,184,0.2)', borderRadius: 16, padding: '24px 20px', position: 'relative', overflow: 'hidden', transition: 'all 0.3s ease', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.border='1px solid rgba(0,212,184,0.4)'; e.currentTarget.style.boxShadow='0 0 20px rgba(0,212,184,0.3), 0 0 40px rgba(0,212,184,0.15)'; e.currentTarget.style.transform='translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.border='1px solid rgba(0,212,184,0.2)'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #00d4b8, #4d9de0)' }} />
            <div className="comparison-title" style={{ fontSize: 16, fontWeight: 600, color: '#00d4b8', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00d4b8', boxShadow: '0 0 8px #00d4b8' }} />
              Choose Netwoke if…
            </div>
            {[
              'You want to understand what your apps are actually doing',
              'You prefer AI explanations over raw IP addresses and port numbers',
              'You want security alerts without needing a CS degree to understand them',
              'You value 100% privacy — zero telemetry, zero cloud, zero accounts',
              'You\'re not a network expert but you care about what\'s on your Mac',
            ].map((text, i) => (
              <div key={i} className="comparison-item" style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#00d4b8', marginTop: 6, flexShrink: 0 }} />
                <span className="comparison-text" style={{ fontSize: 14, color: '#dce8f5', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile layout - stack vertically on small screens */}
        <style>{`
          @media (max-width: 768px) {
            .comparison-grid {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
            }
            .comparison-card {
              padding: 20px 16px !important;
            }
            .comparison-title {
              font-size: 15px !important;
              margin-bottom: 12px !important;
            }
            .comparison-item {
              margin-bottom: 12px !important;
              gap: 8px !important;
            }
            .comparison-text {
              font-size: 14px !important;
              line-height: 1.4 !important;
            }
          }
        `}</style>
      </div>

      {/* ── BOTTOM LINE ── */}
      <div style={{ background: 'rgba(0,0,0,0.4)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '72px 28px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", color: '#00d4b8', letterSpacing: '0.08em', marginBottom: 20 }}>
            The bottom line
          </div>
          <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 38px)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em', marginBottom: 20 }}>
            Other competitors are power tools for network experts.{' '}
            <span style={{ background: 'linear-gradient(90deg, #00d4b8, #4d9de0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Netwoke is smart security for everyone.
            </span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(220,232,245,0.55)', lineHeight: 1.8, fontWeight: 300, marginBottom: 40 }}>
            Netwoke democratizes network security. You get enterprise-level protection and AI-powered intelligence without needing to be a network engineer. It's about understanding your network — not just controlling it.
          </p>

          {/* <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
            <Pill icon="🤖" text="AI-powered explanations" color="#00d4b8" />
            <Pill icon="🔒" text="100% local, zero telemetry" color="#3ecf8e" />
            <Pill icon="⚡" text="One click to kill threats" color="#4d9de0" />
            <Pill icon="💰" text="$11.99 one time" color="#f5a623" />
          </div> */}

          <button onClick={() => window.open('https://buy.stripe.com/eVq7sM6Mi3uwfc8elh04803', '_blank')} style={{
            padding: '14px 32px', borderRadius: 12,
            background: 'linear-gradient(135deg, #00d4b8, #4d9de0)',
            border: 'none', fontSize: 15, fontWeight: 700,
            color: '#04060d', cursor: 'pointer',
            fontFamily: "'IBM Plex Sans', sans-serif",
            boxShadow: '0 0 32px rgba(0,212,184,0.25)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 0 48px rgba(0,212,184,0.35)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 32px rgba(0,212,184,0.25)' }}
          >
            Get Netwoke Today!
          </button>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{ padding: '36px 48px 28px', borderTop: '1px solid rgba(255,255,255,0.07)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ fontSize: 11, color: '#00d4b8', fontFamily: "'IBM Plex Mono', monospace", textAlign: 'center' }}>
          &copy; {new Date().getFullYear()} Netwoke - Where Connection Meets Disclosure.
        </div>
        <div className="nw-footer-links" style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', textAlign: 'center' }}>
          <a href="https://x.com/netwokeapp" target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: 'rgba(220,232,245,0.25)', fontFamily: "'IBM Plex Mono', monospace",
              cursor: 'pointer', transition: 'color .15s', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 5 }}
            onMouseEnter={e => e.currentTarget.style.color='rgba(220,232,245,0.5)'}
            onMouseLeave={e => e.currentTarget.style.color='rgba(220,232,245,0.25)'}>
            𝕏 @NetwokeApp
          </a>
          {['Privacy Policy'].map(l => (
            <span key={l} onClick={() => {
              if (l === 'Privacy Policy') window.location.hash = '#privacy'
            }}
              style={{ fontSize: 11, color: 'rgba(220,232,245,0.25)', fontFamily: "'IBM Plex Mono', monaco",
                cursor: 'pointer', transition: 'color 0.15s',
                textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4
              }}
              onMouseEnter={e => e.currentTarget.style.color='rgba(220,232,245,0.5)'}
              onMouseLeave={e => e.currentTarget.style.color='rgba(220,232,245,0.25)'}>
              {l === 'Veteran Developer' && '🇺🇸 '}
              {l}
            </span>
          ))}
        </div>
      </footer>
      </div>
    </>
  )
}
